<template>
  <div class="surat-keluar-detail">
    <!-- Loading Overlay -->
    <div v-if="pageLoading" class="loading-overlay">
      <a-spin size="large" />
    </div>

    <template v-else-if="suratData && suratData.id && form">
      <div class="page-header">
        <h2 class="page-title">{{ pageTitle }}</h2>
        <p class="page-subtitle">{{ form.nomorSurat }}</p>
        <a-tag :style="getStatusStyle(suratData.status)" class="status-tag">
          <strong>{{
            suratData.status === 'Revisi' ? 'Revisi Verifikator' : suratData.status
          }}</strong>
        </a-tag>
      </div>

      <!-- Action Buttons -->
      <div class="action-buttons">
        <a-button
          v-if="canDeleteOrCancel"
          type="default"
          class="btn-pill-black"
          @click="handleDeleteOrCancel"
          :loading="cancelLoading"
        >
          <template #icon>
            <DeleteOutlined />
          </template>
          {{ suratData.status === 'Draft' ? 'HAPUS DRAFT SURAT' : 'BATALKAN SURAT' }}
        </a-button>

        <a-button
          v-if="suratData.status !== 'Draft'"
          type="default"
          class="btn-pill-black"
          @click="handleDuplicateSurat"
        >
          <template #icon>
            <CopyOutlined />
          </template>
          BUAT SURAT BARU DARI SURAT INI
        </a-button>
      </div>

      <!-- Detail Pengajuan Section (only show when not Draft) -->
      <DetailPengajuan
        v-if="suratData.status !== 'Draft'"
        :creator="suratData.creator"
        :created-at="suratData.createdAt"
        :verifikator="suratData.verifikator"
      />

      <!-- Detail Revisi Section (show revision notes if any) -->
      <DetailRevisi
        v-if="suratData.status !== 'Draft'"
        :verifikator="suratData.verifikator"
        :penandatangan="suratData.penandatangan"
      />

      <!-- Detail Pembatalan Section (only show for Dibatalkan status) -->
      <DetailPembatalan
        :alasan-pembatalan="suratData.alasanPembatalan"
        :status="suratData.status"
      />

      <!-- Form Card -->
      <div class="form-card">
        <h3 class="section-title">Informasi Surat</h3>

        <a-form :model="form" :rules="rules" ref="formRef" layout="vertical">
          <!-- Tambah Surat Keluar Section -->
          <div class="form-section">
            <label class="section-label">Tambah Surat Keluar <span class="required">*</span></label>
            <a-radio-group v-model:value="form.tambahSuratType" class="radio-group-horizontal">
              <a-radio value="buat_baru">Buat Surat Tugas Baru</a-radio>
              <a-radio value="arsipkan">Arsipkan Surat Keluar</a-radio>
            </a-radio-group>
          </div>

          <!-- Jenis Surat Section -->
          <a-form-item
            label="Jenis Surat"
            required
            :validate-status="validationErrors.jenisSurat ? 'error' : ''"
            :help="validationErrors.jenisSurat"
          >
            <a-select
              v-model:value="form.jenisSurat"
              placeholder="Pilih Jenis Surat"
              class="full-width"
              show-search
              :filter-option="filterOption"
              disabled
            >
              <a-select-option value="Surat Tugas" label="Surat Tugas">Surat Tugas</a-select-option>
              <a-select-option value="Cabang" label="Cabang">Cabang</a-select-option>
              <a-select-option value="Farmasi" label="Farmasi">Farmasi</a-select-option>
              <a-select-option value="Instansi Pemerintah" label="Instansi Pemerintah"
                >Instansi Pemerintah</a-select-option
              >
              <a-select-option value="Internal - Surat" label="Internal - Surat"
                >Internal - Surat</a-select-option
              >
              <a-select-option value="Internal - Undangan" label="Internal - Undangan"
                >Internal - Undangan</a-select-option
              >
              <a-select-option value="Kepegawaian PP" label="Kepegawaian PP"
                >Kepegawaian PP</a-select-option
              >
              <a-select-option value="MoU" label="MoU">MoU</a-select-option>
              <a-select-option value="PKS" label="PKS">PKS</a-select-option>
              <a-select-option value="OPKeseminatan Lain" label="OPKeseminatan Lain"
                >OPKeseminatan Lain</a-select-option
              >
              <a-select-option value="PB IDI" label="PB IDI">PB IDI</a-select-option>
              <a-select-option value="Perusahaan Swasta" label="Perusahaan Swasta"
                >Perusahaan Swasta</a-select-option
              >
              <a-select-option value="Podcast" label="Podcast">Podcast</a-select-option>
              <a-select-option value="Pokja" label="Pokja">Pokja</a-select-option>
              <a-select-option value="Rumah Sakit" label="Rumah Sakit">Rumah Sakit</a-select-option>
              <a-select-option value="SK" label="SK">SK</a-select-option>
              <a-select-option value="YJI" label="YJI">YJI</a-select-option>
              <a-select-option value="Lainnya" label="Lainnya">Lainnya</a-select-option>
            </a-select>
          </a-form-item>

          <!-- Metode Pembuatan Section (only for non-Surat Tugas) -->
          <template v-if="isNotSuratTugas">
            <MetodePembuatanRadio v-model="form.metodePembuatan" />

            <!-- File Upload Section (only when Upload File is selected) -->
            <template v-if="isUploadFile">
              <div class="form-section upload-section">
                <label class="section-label"
                  >Upload Dokumen Surat <span class="required">*</span></label
                >
                <p class="section-description">
                  Upload file surat yang sudah jadi (sudah ditandatangani)
                </p>

                <!-- Custom Upload Area -->
                <div
                  class="upload-area"
                  @click="triggerFileInput"
                  @drop.prevent="handleFileDrop"
                  @dragover.prevent="handleDragOver"
                  @dragleave.prevent="handleDragLeave"
                  @dragenter.prevent
                >
                  <input
                    ref="fileInputRef"
                    type="file"
                    accept=".pdf,.png,.jpg,.jpeg"
                    multiple
                    style="display: none"
                    @change="handleFileChange"
                  />
                  <div class="upload-icon">
                    <svg
                      width="48"
                      height="48"
                      viewBox="0 0 48 48"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                    >
                      <path
                        d="M24 8V32M24 8L16 16M24 8L32 16M40 32V40H8V32"
                        stroke="#CCCCCC"
                        stroke-width="2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      />
                    </svg>
                  </div>
                  <p class="upload-text">Klik atau seret file ke sini untuk mengunggah</p>
                  <p class="upload-hint">
                    Upload satu atau lebih file dalam format PDF, PNG, atau JPG/JPEG (maksimal 10MB
                    per file)
                  </p>
                </div>

                <!-- Uploaded Files Display -->
                <div v-if="uploadedFiles.length > 0" class="uploaded-files-list">
                  <div
                    v-for="(file, index) in uploadedFiles"
                    :key="file.uid || index"
                    class="uploaded-file-item"
                  >
                    <PaperClipOutlined class="file-icon" />
                    <a :href="getUploadedFileUrl(file)" target="_blank" class="file-name-link">{{
                      file.name
                    }}</a>
                    <a-button
                      type="text"
                      danger
                      size="small"
                      @click="removeUploadedFile(index)"
                      class="btn-remove-file"
                    >
                      <template #icon>
                        <CloseOutlined />
                      </template>
                    </a-button>
                  </div>
                </div>
              </div>
            </template>
          </template>

          <!-- Nomor Surat Section -->
          <!-- Auto-generated for all except "Lainnya", Manual input for "Lainnya" -->
          <template v-if="form.jenisSurat === 'Lainnya'">
            <!-- Manual Input for Lainnya -->
            <a-form-item
              label="Nomor Surat"
              required
              :validate-status="nomorSuratError || validationErrors.nomorSurat ? 'error' : ''"
              :help="nomorSuratError || validationErrors.nomorSurat"
            >
              <a-input
                v-model:value="form.nomorSurat"
                class="full-width"
                placeholder="Nomor surat keluar"
                @blur="checkNomorSuratUnique"
              />
              <p
                v-if="nomorSuratChecking"
                class="checking-hint"
                style="margin-top: 4px; margin-bottom: 0; color: #1890ff;"
              >
                Memeriksa nomor surat...
              </p>
            </a-form-item>
          </template>
          <template v-else>
            <!-- Auto-generated with increment/decrement -->
            <NomorSuratInput
              v-model="form.nomorSurat"
              :can-decrement="canDecrement"
              :error="nomorSuratError"
              :checking="nomorSuratChecking"
              @increment="() => incrementNomorSurat(form.jenisSurat)"
              @decrement="() => decrementNomorSurat(form.jenisSurat)"
            />
          </template>

          <!-- Sifat Surat Section -->
          <SifatSuratRadio v-model="form.sifatSurat" />

          <!-- Bentuk Surat Section - hide when Upload File is selected -->
          <template v-if="!isUploadFile">
            <BentukSuratRadio v-model="form.bentukSurat" />
          </template>

          <!-- ==================== SURAT TUGAS SPECIFIC FIELDS ==================== -->
          <template v-if="isSuratTugas">
            <!-- Surat Undangan Acara Section -->
            <div class="form-section">
              <label class="section-label">Surat Undangan Acara</label>
              <a-select
                v-model:value="form.suratUndanganAcaraId"
                placeholder="Pilih undangan acara"
                :options="undanganAcaraOptions"
                class="full-width"
                allow-clear
                show-search
                :filter-option="filterOption"
              />
            </div>

            <!-- Two Column Layout -->
            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item
                  label="Nama Kegiatan"
                  required
                  :validate-status="validationErrors.namaKegiatan ? 'error' : ''"
                  :help="validationErrors.namaKegiatan"
                >
                  <a-input v-model:value="form.namaKegiatan" placeholder="Masukkan nama kegiatan" />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item
                  label="Waktu Perjalanan Mulai"
                  required
                  :validate-status="validationErrors.waktuPerjalananMulai ? 'error' : ''"
                  :help="validationErrors.waktuPerjalananMulai"
                >
                  <a-date-picker
                    v-model:value="form.waktuPerjalananMulai"
                    :show-time="{ format: 'HH:mm' }"
                    format="DD-MM-YYYY HH:mm"
                    placeholder="Pilih waktu mulai"
                    class="full-width"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item
                  label="Waktu Perjalanan Selesai"
                  :validate-status="validationErrors.waktuPerjalananSelesai ? 'error' : ''"
                  :help="validationErrors.waktuPerjalananSelesai"
                >
                  <a-date-picker
                    v-model:value="form.waktuPerjalananSelesai"
                    :show-time="{ format: 'HH:mm' }"
                    format="DD-MM-YYYY HH:mm"
                    placeholder="Pilih waktu selesai"
                    class="full-width"
                    :disabled-date="
                      d =>
                        form.waktuPerjalananMulai
                          ? d && d.isBefore(form.waktuPerjalananMulai, 'day')
                          : false
                    "
                    @change="
                      val => {
                        if (
                          val &&
                          form.waktuPerjalananMulai &&
                          val.isBefore(form.waktuPerjalananMulai)
                        ) {
                          validationErrors.value.waktuPerjalananSelesai =
                            'Waktu selesai tidak boleh sebelum waktu mulai'
                        } else {
                          validationErrors.value.waktuPerjalananSelesai = ''
                        }
                      }
                    "
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <a-row :gutter="24">
              <a-col :span="12">
                <a-form-item
                  label="Tempat Kegiatan"
                  required
                  :validate-status="validationErrors.tempatKegiatan ? 'error' : ''"
                  :help="validationErrors.tempatKegiatan"
                >
                  <a-input
                    v-model:value="form.tempatKegiatan"
                    placeholder="Masukkan tempat kegiatan"
                  />
                </a-form-item>
              </a-col>
              <a-col :span="12">
                <a-form-item
                  label="Tanggal Surat"
                  required
                  :validate-status="validationErrors.tanggalSurat ? 'error' : ''"
                  :help="validationErrors.tanggalSurat"
                >
                  <a-date-picker
                    v-model:value="form.tanggalSurat"
                    format="DD-MM-YYYY"
                    placeholder="Pilih tanggal surat"
                    class="full-width"
                  />
                </a-form-item>
              </a-col>
            </a-row>

            <!-- Penandatangan Section -->
            <PenandatanganSelect
              v-model:kiri-value="form.penandatanganKiriId"
              v-model:kanan-value="form.penandatanganKananId"
              :kiri-error="validationErrors.penandatanganKiri"
            />

            <!-- Keperluan Section -->
            <a-form-item
              label="Keperluan"
              required
              :validate-status="validationErrors.keperluan ? 'error' : ''"
              :help="validationErrors.keperluan"
            >
              <a-textarea v-model:value="form.keperluan" placeholder="Keperluan dinas" :rows="3" />
            </a-form-item>

            <!-- Perwakilan Dinas Table -->
            <a-form-item
              :validate-status="validationErrors.perwakilanDinas ? 'error' : ''"
              :help="validationErrors.perwakilanDinas"
            >
              <PerwakilanDinasTable
                :data-source="form.perwakilanDinas"
                :show-pdf-button="suratData.status !== 'Draft'"
                :disabled="isArchived"
                @add="addPerwakilanRow"
                @remove="removePerwakilanRow"
                @preview="handlePreviewPerwakilan"
              />
            </a-form-item>
          </template>

          <!-- ==================== SURAT LAINNYA SPECIFIC FIELDS ==================== -->
          <template v-else>
            <!-- Surat Undangan Acara Section -->
            <div class="form-section">
              <label class="section-label label-grey">Surat Undangan Acara</label>
              <a-select
                v-model:value="form.suratUndanganAcaraId"
                placeholder="Pilih surat masuk terkait (Opsional)"
                :options="undanganAcaraOptions"
                class="full-width"
                allow-clear
                show-search
                :filter-option="filterOption"
              />
            </div>

            <!-- Tanggal Surat -->
            <a-form-item
              label="Tanggal Surat"
              required
              :validate-status="validationErrors.tanggalSurat ? 'error' : ''"
              :help="validationErrors.tanggalSurat"
            >
              <a-date-picker
                v-model:value="form.tanggalSurat"
                format="DD-MM-YYYY"
                placeholder="Pilih tanggal surat"
                class="full-width"
              />
            </a-form-item>

            <!-- Perihal Section (serves as namaKegiatan/title for all non-Surat Tugas types) -->
            <a-form-item
              label="Perihal"
              required
              :validate-status="validationErrors.perihal ? 'error' : ''"
              :help="validationErrors.perihal"
            >
              <a-input
                v-model:value="form.namaKegiatan"
                placeholder="Perihal surat yang akan ditampilkan di tabel list surat keluar"
              />
            </a-form-item>

            <!-- Penandatangan Section -->
            <PenandatanganSelect
              v-model:kiri-value="form.penandatanganKiriId"
              v-model:kanan-value="form.penandatanganKananId"
              :kiri-error="validationErrors.penandatanganKiri"
            />

            <!-- Isi Surat Section with TinyMCE - only show when NOT Upload File -->
            <template v-if="!isUploadFile">
              <a-form-item
                label="Isi Surat"
                required
                :validate-status="validationErrors.body ? 'error' : ''"
                :help="validationErrors.body"
              >
                <template #extra>
                  <p class="section-description" style="margin-top: 4px; margin-bottom: 8px;">
                    Isi bagian ini dengan teks yang ingin ditampilkan sebagai isi surat (tidak perlu
                    menulis kop surat dan area tanda tangan)
                  </p>
                </template>
                <div class="tinymce-container" :class="{ 'has-error': validationErrors.body }">
                  <Editor v-model="form.body" :api-key="apiKey" :init="suratLainnyaInit" />
                </div>
              </a-form-item>

              <!-- Preview Button -->
              <div class="preview-section">
                <a-button type="default" class="btn-preview" @click="handlePreviewLainnya">
                  <template #icon>
                    <EyeOutlined />
                  </template>
                  LIHAT PREVIEW SURAT
                </a-button>
              </div>
            </template>
          </template>

          <!-- Verifikator Table -->
          <a-form-item
            :validate-status="validationErrors.verifikator ? 'error' : ''"
            :help="validationErrors.verifikator"
          >
            <VerifikatorTable
              class="verifikator-form-item"
              :data-source="form.verifikator"
              :locked-ids="lockedVerifikatorIds"
              :hide-delete-for-locked="shouldHideVerifikatorDelete"
              @add="addVerifikatorRow"
              @remove="removeVerifikatorRow"
            />
          </a-form-item>

          <!-- Alur Verifikasi Section -->
          <AlurVerifikasi class="alur-verifikasi-tight" :verifikator="form.verifikator" />

          <!-- Submit Buttons -->
          <div class="submit-section">
            <template v-if="suratData.status === 'Draft'">
              <a-button
                type="primary"
                class="btn-simpan"
                @click="handleSubmit"
                :loading="submitLoading"
                :disabled="isLainnya && !!nomorSuratError"
              >
                SIMPAN
              </a-button>
              <a-button
                type="default"
                class="btn-draft"
                @click="handleSaveAsDraft"
                :loading="draftLoading"
                :disabled="isLainnya && !!nomorSuratError"
              >
                SIMPAN SEBAGAI DRAFT
              </a-button>
            </template>
            <template v-else>
              <a-button
                type="primary"
                class="btn-simpan"
                @click="handleSaveChanges"
                :loading="submitLoading"
                :disabled="isLainnya && !!nomorSuratError"
              >
                SIMPAN PERUBAHAN
              </a-button>
            </template>
          </div>
        </a-form>
      </div>
    </template>

    <!-- Empty State -->
    <template v-else-if="!pageLoading">
      <div class="empty-state">
        <a-result
          status="404"
          title="Surat Tidak Ditemukan"
          sub-title="Surat keluar yang Anda cari tidak ditemukan atau telah dihapus."
        >
          <template #extra>
            <a-button type="primary" @click="router.push('/surat-keluar')">
              Kembali ke Daftar Surat
            </a-button>
          </template>
        </a-result>
      </div>
    </template>

    <!-- Cancel/Delete Surat Modal -->
    <a-modal
      v-model:visible="cancelModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="cancel-modal"
    >
      <template #title>
        <strong>{{
          isDraft ? 'Hapus draft surat?' : 'Yakin ingin menghentikan proses surat keluar?'
        }}</strong>
      </template>
      <p class="modal-description">
        <template v-if="isDraft">
          Anda dapat mengubah atau menghapus draft surat nanti. Draft surat tidak akan dikirim ke
          verifikator atau penandatanganan.
        </template>
        <template v-else>
          Tindakan ini akan membatalkan surat keluar dan proses verifikasi dan/atau penandatanganan
          surat akan dihentikan. Surat yang sudah dibatalkan tidak dapat digunakan kembali.
        </template>
      </p>
      <a-form layout="vertical">
        <a-form-item v-if="!isDraft" label="Alasan Pembatalan" class="cancel-reason-field" required>
          <a-textarea
            v-model:value="cancelReason"
            placeholder="Masukkan alasan pembatalan surat"
            :rows="3"
          />
        </a-form-item>
      </a-form>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="cancelModalVisible = false">
          KEMBALI
        </a-button>
        <a-button class="btn-pill-red" :loading="cancelLoading" @click="confirmDeleteOrCancel">
          {{ isDraft ? 'YA, HAPUS' : 'YA, BATALKAN SURAT' }}
        </a-button>
      </div>
    </a-modal>

    <!-- Update Confirmation Modal -->
    <a-modal
      v-model:visible="updateModalVisible"
      :footer="null"
      :mask-closable="false"
      centered
      class="confirm-modal"
    >
      <template #title>
        <strong>Ubah Data Surat Keluar?</strong>
      </template>
      <p class="modal-description">
        Mohon cek kembali perubahan data surat sebelum menyimpan formulir.
      </p>
      <div class="modal-buttons">
        <a-button type="default" class="btn-pill-black" @click="updateModalVisible = false">
          KEMBALI
        </a-button>
        <a-button
          type="primary"
          class="btn-pill-red"
          :loading="submitLoading"
          @click="confirmUpdate"
        >
          YA, SIMPAN
        </a-button>
      </div>
    </a-modal>

    <!-- PDF Preview Modal -->
    <PDFPreviewModal
      v-model:visible="pdfModalVisible"
      :title="pdfModalTitle"
      :pdf-path="pdfModalPath"
    />
  </div>
</template>

<script>
import { defineComponent, ref, reactive, onMounted, computed, watch, h, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useStore } from 'vuex'
import { notification, Modal } from 'ant-design-vue'
import {
  DeleteOutlined,
  CopyOutlined,
  EyeOutlined,
  PaperClipOutlined,
  CloseOutlined,
} from '@ant-design/icons-vue'
import { suratKeluarAPI, getFileUrl } from '@/services/axios/perki'
import moment from 'moment'
import 'moment/locale/id'

// Import components
import {
  PerwakilanDinasTable,
  VerifikatorTable,
  NomorSuratInput,
  DetailPengajuan,
  DetailRevisi,
  DetailPembatalan,
  SifatSuratRadio,
  BentukSuratRadio,
  MetodePembuatanRadio,
  PenandatanganSelect,
  AlurVerifikasi,
  PDFPreviewModal,
} from '@/components/SuratKeluar'

// Import other composables
import {
  useNomorSurat,
  useSuratKeluarValidation,
  useTinyMCE,
  useDateFormatting,
} from '@/composables'
import { useUnsavedChangesWarning } from '@/composables/useUnsavedChangesWarning'

// Import TinyMCE Editor component
import Editor from '@tinymce/tinymce-vue'

moment.locale('id')

export default defineComponent({
  name: 'SuratKeluarDetail',
  components: {
    DeleteOutlined,
    CopyOutlined,
    EyeOutlined,
    PaperClipOutlined,
    CloseOutlined,
    PerwakilanDinasTable,
    VerifikatorTable,
    DetailPengajuan,
    DetailRevisi,
    DetailPembatalan,
    AlurVerifikasi,
    NomorSuratInput,
    SifatSuratRadio,
    BentukSuratRadio,
    MetodePembuatanRadio,
    PenandatanganSelect,
    Editor, // Register TinyMCE Editor component
    PDFPreviewModal,
  },
  setup() {
    const router = useRouter()
    const route = useRoute()
    const store = useStore()
    const formRef = ref(null)
    const pageLoading = ref(true)
    const isLoading = ref(false)
    const cancelLoading = ref(false)
    const cancelModalVisible = ref(false)
    const cancelReason = ref('')
    const updateModalVisible = ref(false)
    const formInitialized = ref(false) // Track if form has been populated from backend
    const fileInputRef = ref(null) // File input ref for upload

    // PDF Preview Modal state
    const pdfModalVisible = ref(false)
    const pdfModalTitle = ref('Preview Surat Keluar')
    const pdfModalPath = ref('')
    let currentBlobUrl = null // Track current blob URL for cleanup

    // Get current user info for access control
    const currentUser = JSON.parse(localStorage.getItem('perki_user') || '{}')
    const currentUserId = currentUser.id
    const currentUserRole = localStorage.getItem('perki_role') || currentUser.role

    // TinyMCE setup
    const { apiKey, suratLainnyaInit } = useTinyMCE()

    // Date formatting utilities
    const { formatForAPI, formatDateTimeForAPI } = useDateFormatting()

    // Unsaved changes warning
    const originalFormData = ref(null)
    const hasFormChanges = computed(() => {
      if (!originalFormData.value) return false
      return JSON.stringify(form) !== JSON.stringify(originalFormData.value)
    })
    const { setHasUnsavedChanges, resetUnsavedChanges } = useUnsavedChangesWarning({
      hasFormChanges,
    })

    // Get loading states from store
    const submitLoading = computed(() => store.state.suratKeluar.submitLoading)
    const draftLoading = computed(() => store.state.suratKeluar.draftLoading)

    // Original surat data from store
    const suratData = computed(() => store.state.suratKeluar.currentSurat || {})

    // Check if this is an archived surat
    const isArchived = computed(() => suratData.value.isArchived === true)

    // Undangan Acara options (Surat Masuk references)
    const undanganAcaraOptions = computed(() => store.state.suratKeluar.suratMasukOptions)

    // Use composable for nomor surat
    const {
      nomorSurat: nomorSuratRef,
      canDecrement,
      fetchLatestNomorSurat,
      setNomorSuratForEdit,
      incrementNomorSurat: incrementNomorSuratComposable,
      decrementNomorSurat: decrementNomorSuratComposable,
    } = useNomorSurat()

    // Use validation composable
    const {
      nomorSuratError,
      nomorSuratChecking,
      checkNomorSuratUnique: checkNomorSuratUniqueComposable,
      clearNomorSuratError,
    } = useSuratKeluarValidation()

    // Check nomor surat uniqueness (for Lainnya type)
    const checkNomorSuratUnique = async () => {
      if (isLainnya.value && form.nomorSurat) {
        const currentId = route.params.id
        await checkNomorSuratUniqueComposable(form.nomorSurat, currentId)
      }
    }

    // Computed: Check if this is Surat Tugas
    const isSuratTugas = computed(() => {
      return form.jenisSurat === 'Surat Tugas'
    })

    // Computed: Check if this is NOT Surat Tugas (for Metode Pembuatan section)
    const isNotSuratTugas = computed(() => {
      return form.jenisSurat && form.jenisSurat !== 'Surat Tugas'
    })

    // Computed: Check if Upload File method is selected
    const isUploadFile = computed(() => {
      return form.metodePembuatan === 'Upload File'
    })

    // Computed: Check if this is Lainnya (manual nomor entry)
    const isLainnya = computed(() => {
      return form.jenisSurat === 'Lainnya'
    })

    // Page title based on status
    const pageTitle = computed(() => {
      if (suratData.value.status === 'Draft') {
        return 'Draft Surat Keluar'
      }
      return 'Ubah Surat Keluar'
    })

    // Check if current status is Draft
    const isDraft = computed(() => suratData.value.status === 'Draft')

    // Should hide delete button for locked verifikator (only allow in Draft status)
    const shouldHideVerifikatorDelete = computed(() => {
      return !isDraft.value // Hide delete for locked rows when NOT in Draft
    })

    // Check if current user is the owner (pengaju surat)
    const isOwner = computed(() => {
      return suratData.value.creator?.id === currentUserId
    })

    // Check if user can perform actions (only sekretaris who owns it, or super_admin)
    const canPerformActions = computed(() => {
      // Only super_admin has full access
      if (currentUserRole === 'super_admin') {
        return true
      }
      // Sekretaris can only perform actions on their own surat
      return currentUserRole === 'sekretaris' && isOwner.value
    })

    // Can delete or cancel - Draft can be deleted, other allowed statuses can be cancelled
    // Only owner sekretaris or super_admin can perform these actions
    const canDeleteOrCancel = computed(() => {
      const allowedStatuses = [
        'Draft',
        'Verifikasi',
        'Revisi',
        'Revisi Penandatanganan',
        'Penandatanganan',
        'Belum Dikirim',
      ]
      return allowedStatuses.includes(suratData.value.status) && canPerformActions.value
    })

    // Get status style (custom background and text colors)
    const getStatusStyle = status => {
      return store.getters['suratKeluar/getStatusStyle'](status)
    }

    // Validation errors state
    const validationErrors = ref({})

    // Track locked verifikator IDs (existing verifikator that shouldn't be deleted except in Draft)
    const lockedVerifikatorIds = ref([])

    // Generate temporary ID for new rows
    // Helper to extract filename from path
    const getFileNameFromPath = path => {
      if (!path) return ''
      const parts = path.split('/')
      return parts[parts.length - 1]
    }

    const generateTempId = () => {
      return (
        'temp-' +
        Date.now() +
        '-' +
        Math.random()
          .toString(36)
          .substr(2, 9)
      )
    }

    // Form data
    const form = reactive({
      tambahSuratType: 'buat_baru',
      jenisSurat: null,
      nomorSurat: '',
      sifatSurat: 'Biasa',
      bentukSurat: 'Soft copy',
      metodePembuatan: 'Buat di Sistem', // New field for creation method
      suratUndanganAcaraId: null,
      namaKegiatan: '', // For Surat Tugas: activity name, For Lainnya: perihal/title
      waktuPerjalananMulai: null,
      waktuPerjalananSelesai: null,
      tempatKegiatan: '',
      tanggalSurat: null,
      penandatanganKiriId: null,
      penandatanganKananId: null,
      keperluan: '', // For Surat Tugas only
      body: '', // For Surat Lainnya - HTML content from CKEditor
      pdfPath: null, // For Surat Lainnya - single PDF path
      perwakilanDinas: [],
      verifikator: [],
    })

    // Uploaded files for "Upload File" metode pembuatan
    const uploadedFiles = ref([])

    // Validation rules
    const rules = {
      jenisSurat: [{ required: true, message: 'Jenis surat harus dipilih' }],
      nomorSurat: [{ required: true, message: 'Nomor surat harus diisi' }],
      sifatSurat: [{ required: true, message: 'Sifat surat harus dipilih' }],
      bentukSurat: [{ required: true, message: 'Bentuk surat harus dipilih' }],
      namaKegiatan: [{ required: true, message: 'Nama kegiatan harus diisi' }],
      waktuPerjalananMulai: [{ required: true, message: 'Waktu perjalanan mulai harus dipilih' }],
      waktuPerjalananSelesai: [
        { required: false, message: 'Waktu perjalanan selesai harus dipilih' },
      ],
      tempatKegiatan: [{ required: true, message: 'Tempat kegiatan harus diisi' }],
      tanggalSurat: [{ required: true, message: 'Tanggal surat harus dipilih' }],
      penandatanganKiriId: [{ required: true, message: 'Penandatangan 1 harus dipilih' }],
      keperluan: [{ required: true, message: 'Keperluan harus diisi' }],
    }

    // Populate form from surat data
    const populateForm = data => {
      // Save original data for comparison
      originalFormData.value = null
      form.jenisSurat = data.jenisSurat
      form.nomorSurat = data.nomorSurat
      form.sifatSurat = data.sifatSurat
      form.bentukSurat = data.bentukSurat
      form.metodePembuatan = data.metodePembuatan || 'Buat di Sistem'
      form.suratUndanganAcaraId = data.suratUndanganAcaraId || null
      form.namaKegiatan = data.namaKegiatan || ''
      // Handle new range fields, fallback to old waktuPerjalanan for backward compatibility
      if (data.waktuPerjalananMulai) {
        form.waktuPerjalananMulai = moment(data.waktuPerjalananMulai)
      } else if (data.waktuPerjalanan) {
        // Fallback to old field for backward compatibility
        form.waktuPerjalananMulai = moment(data.waktuPerjalanan)
      } else {
        form.waktuPerjalananMulai = null
      }
      form.waktuPerjalananSelesai = data.waktuPerjalananSelesai
        ? moment(data.waktuPerjalananSelesai)
        : null
      form.tempatKegiatan = data.tempatKegiatan || ''
      form.tanggalSurat = data.tanggalSurat ? moment(data.tanggalSurat) : null
      form.penandatanganKiriId = data.penandatanganKiriId
      form.penandatanganKananId = data.penandatanganKananId || null
      form.keperluan = data.keperluan || ''
      form.body = data.body || '' // For Surat Lainnya
      form.pdfPath = data.pdfPath || null // For Surat Lainnya single PDF

      // Populate uploaded files from archivedPdfPaths (for Upload File method)
      if (data.archivedPdfPaths) {
        try {
          const paths = JSON.parse(data.archivedPdfPaths)
          if (Array.isArray(paths) && paths.length > 0) {
            uploadedFiles.value = paths.map((path, index) => ({
              uid: `existing-${index}`,
              name: getFileNameFromPath(path),
              path: path,
              url: getFileUrl(path),
              status: 'done',
              isExisting: true, // Mark as existing file
            }))
          }
        } catch (e) {
          console.error('Failed to parse archivedPdfPaths:', e)
        }
      }

      // Populate perwakilan dinas (only for Surat Tugas)
      if (data.perwakilanDinas && data.perwakilanDinas.length > 0) {
        form.perwakilanDinas = data.perwakilanDinas.map(item => ({
          id: item.id,
          userId: item.userId,
          isNonPengurus: item.isNonPengurus || false,
          namaNonPengurus: item.namaNonPengurus || null,
          jabatanNonPengurus: item.jabatanNonPengurus || null,
          npaNonPengurus: item.npaNonPengurus || null,
          searchValue: item.isNonPengurus ? item.namaNonPengurus : item.user?.name || '',
          pdfPath: item.pdfPath || null,
          user: item.user || null,
        }))
      } else {
        form.perwakilanDinas = [
          {
            id: generateTempId(),
            userId: null,
            isNonPengurus: false,
            namaNonPengurus: null,
            jabatanNonPengurus: null,
            npaNonPengurus: null,
            searchValue: '',
            pdfPath: null,
          },
        ]
      }

      // Populate verifikator
      if (data.verifikator && data.verifikator.length > 0) {
        // Sort by urutan to maintain correct order
        const sortedVerifikator = [...data.verifikator].sort((a, b) => a.urutan - b.urutan)
        form.verifikator = sortedVerifikator.map(item => ({
          id: item.id,
          userId: item.userId,
          urutan: item.urutan,
          status: item.status,
          user: item.user,
        }))
        // Track locked verifikator IDs (existing verifikator from backend)
        lockedVerifikatorIds.value = sortedVerifikator.map(item => item.id)
      } else {
        form.verifikator = [{ id: generateTempId(), userId: null, urutan: 1 }]
        lockedVerifikatorIds.value = []
      }

      // Save snapshot after population
      setTimeout(() => {
        originalFormData.value = JSON.parse(JSON.stringify(form))
      }, 100)
    }

    // Add perwakilan row
    const addPerwakilanRow = () => {
      form.perwakilanDinas.push({
        id: generateTempId(),
        userId: null,
        isNonPengurus: false,
        namaNonPengurus: null,
        jabatanNonPengurus: null,
        npaNonPengurus: null,
        searchValue: '',
        pdfPath: null,
      })
    }

    // Remove perwakilan row
    const removePerwakilanRow = index => {
      if (form.perwakilanDinas.length > 1) {
        form.perwakilanDinas.splice(index, 1)
      } else {
        notification.warning({ message: 'Minimal harus ada 1 perwakilan dinas' })
      }
    }

    // Add verifikator row
    const addVerifikatorRow = () => {
      const nextUrutan = form.verifikator.length + 1
      form.verifikator.push({
        id: generateTempId(),
        userId: null,
        urutan: nextUrutan,
      })
    }

    // Remove verifikator row
    const removeVerifikatorRow = index => {
      form.verifikator.splice(index, 1)
      // Update urutan for remaining items
      form.verifikator.forEach((item, idx) => {
        item.urutan = idx + 1
      })
    }

    // ==================== FILE UPLOAD HANDLERS ====================
    // Trigger file input click
    const triggerFileInput = () => {
      fileInputRef.value?.click()
    }

    // Handle drag over
    const handleDragOver = e => {
      e.currentTarget.classList.add('drag-over')
    }

    // Handle drag leave
    const handleDragLeave = e => {
      e.currentTarget.classList.remove('drag-over')
    }

    // Handle file drop
    const handleFileDrop = e => {
      e.currentTarget.classList.remove('drag-over')
      const files = Array.from(e.dataTransfer.files)
      addFilesToList(files)
    }

    // Handle file change (from input)
    const handleFileChange = e => {
      const files = Array.from(e.target.files)
      addFilesToList(files)
      // Reset input so same file can be selected again
      e.target.value = ''
    }

    // Add files to upload list with validation
    const addFilesToList = files => {
      const validTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg']
      const maxSize = 10 * 1024 * 1024 // 10MB

      files.forEach(file => {
        // Validate file type
        if (!validTypes.includes(file.type)) {
          notification.error({
            message: 'File tidak valid',
            description: `${file.name} bukan format PDF, PNG, atau JPG/JPEG`,
          })
          return
        }

        // Validate file size
        if (file.size > maxSize) {
          notification.error({
            message: 'File terlalu besar',
            description: `${file.name} melebihi batas 10MB`,
          })
          return
        }

        // Add to list with unique id
        uploadedFiles.value.push({
          uid: `${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}`,
          name: file.name,
          file: file, // Store the actual File object
        })
      })
    }

    // Remove uploaded file
    const removeUploadedFile = index => {
      uploadedFiles.value.splice(index, 1)
    }

    // Get file URL for preview
    const getUploadedFileUrl = file => {
      // For existing files (from server), use the url property
      if (file.url) {
        return file.url
      }
      // For newly uploaded files, create object URL
      if (file.file) {
        return URL.createObjectURL(file.file)
      }
      return '#'
    }

    // Increment nomor surat - wrapper for composable
    const incrementNomorSurat = async () => {
      // Clear previous error
      clearNomorSuratError()

      // Increment the number
      incrementNomorSuratComposable(form.jenisSurat)

      // Sync the composable's reactive value to form
      form.nomorSurat = nomorSuratRef.value

      // Check if this nomor is already used by another surat
      const currentId = route.params.id
      await checkNomorSuratUniqueComposable(form.nomorSurat, currentId)
    }

    // Decrement nomor surat - wrapper for composable
    const decrementNomorSurat = async () => {
      // Clear previous error
      clearNomorSuratError()

      // Decrement the number
      decrementNomorSuratComposable(form.jenisSurat)

      // Sync the composable's reactive value to form
      form.nomorSurat = nomorSuratRef.value

      // Check if this nomor is already used by another surat
      const currentId = route.params.id
      await checkNomorSuratUniqueComposable(form.nomorSurat, currentId)
    }

    // Filter option for select
    const filterOption = (input, option) => {
      const label = option.label || option.value || ''
      return label.toLowerCase().indexOf(input.toLowerCase()) >= 0
    }

    // Handle preview for Perwakilan Dinas row
    const handlePreviewPerwakilan = async ({ index, record }) => {
      // Validate: must have either userId (pengurus) or non-pengurus data
      if (!record.userId && !record.isNonPengurus) {
        notification.warning({ message: 'Silakan pilih perwakilan dinas terlebih dahulu' })
        return
      }

      // For non-pengurus, validate required fields
      if (record.isNonPengurus) {
        if (!record.namaNonPengurus || !record.jabatanNonPengurus || !record.npaNonPengurus) {
          notification.warning({
            message: 'Data non-pengurus belum lengkap',
            description: 'Silakan lengkapi Nama, Jabatan, dan NPA untuk non-pengurus',
          })
          return
        }
      }

      // Minimal validation - only require perwakilan to be selected
      // Preview will show whatever fields are currently filled
      if (!form.namaKegiatan) {
        notification.warning({
          message: 'Nama Kegiatan diperlukan',
          description: 'Minimal isi Nama Kegiatan untuk melihat preview surat',
        })
        return
      }
      isLoading.value = false

      try {
        isLoading.value = true

        // Prepare preview data - send whatever is currently filled
        const previewData = {
          jenisSurat: form.jenisSurat || 'Surat Tugas',
          nomorSurat: form.nomorSurat || '[NOMOR BELUM DIISI]',
          sifatSurat: form.sifatSurat || 'Biasa',
          bentukSurat: form.bentukSurat || 'Soft Copy',
          tanggalSurat: form.tanggalSurat ? form.tanggalSurat.format('DD-MM-YYYY') : null,
          namaKegiatan: form.namaKegiatan,
          waktuPerjalananMulai: formatDateTimeForAPI(form.waktuPerjalananMulai),
          waktuPerjalananSelesai: formatDateTimeForAPI(form.waktuPerjalananSelesai),
          tempatKegiatan: form.tempatKegiatan || null,
          keperluan: form.keperluan || null,
          penandatanganKiriId: form.penandatanganKiriId || null,
          penandatanganKananId: form.penandatanganKananId || null,
        }

        // Add either pengurus or non-pengurus data
        if (record.isNonPengurus) {
          previewData.isNonPengurus = true
          previewData.namaNonPengurus = record.namaNonPengurus
          previewData.jabatanNonPengurus = record.jabatanNonPengurus
          previewData.npaNonPengurus = record.npaNonPengurus
        } else {
          previewData.perwakilanDinasUserId = record.userId
        }

        // Call preview API
        const response = await suratKeluarAPI.previewPerwakilanDinasPDF(previewData)

        // Revoke previous blob URL if exists
        if (currentBlobUrl) {
          window.URL.revokeObjectURL(currentBlobUrl)
          currentBlobUrl = null
        }

        // Create blob URL for modal preview
        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)
        currentBlobUrl = blobUrl // Store for cleanup

        // Get perwakilan name for modal title
        let perwakilanName = 'Perwakilan'
        if (record.isNonPengurus) {
          perwakilanName = record.namaNonPengurus || 'Non-Pengurus'
        } else {
          const pengurusMap = store.state.suratKeluar.pengurusMap || {}
          const perwakilan = pengurusMap[record.userId]
          perwakilanName = perwakilan?.name || 'Perwakilan'
        }

        // IMPORTANT: Close modal first to ensure clean state
        pdfModalVisible.value = false

        // Wait for modal to fully close, then set path and open
        await nextTick()

        pdfModalTitle.value = `Preview Surat Tugas - ${perwakilanName}`
        pdfModalPath.value = blobUrl

        // Small delay to ensure path is set before opening
        await nextTick()

        pdfModalVisible.value = true
      } catch (error) {
        console.error('Preview error:', error)
        notification.error({
          message: 'Gagal membuat preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
        })
      } finally {
        isLoading.value = false
      }
    }

    // Comprehensive validation function with inline error tracking
    const validateAllFields = () => {
      validationErrors.value = {} // Clear previous errors
      let hasErrors = false

      // Common validation for all types
      if (!form.nomorSurat || form.nomorSurat.trim() === '') {
        validationErrors.value.nomorSurat = 'Nomor surat wajib diisi'
        hasErrors = true
      }

      // Check for nomor surat uniqueness error (Lainnya type)
      if (isLainnya.value && nomorSuratError.value) {
        hasErrors = true
      }

      if (!form.tanggalSurat) {
        validationErrors.value.tanggalSurat = 'Tanggal surat wajib dipilih'
        hasErrors = true
      }

      if (!form.penandatanganKiriId) {
        validationErrors.value.penandatanganKiri = 'Penandatangan kiri wajib dipilih'
        hasErrors = true
      }

      // Type-specific validation (3-way logic)
      if (isSuratTugas.value) {
        // Surat Tugas validation
        if (!form.namaKegiatan || form.namaKegiatan.trim() === '') {
          validationErrors.value.namaKegiatan = 'Nama kegiatan wajib diisi'
          hasErrors = true
        }

        if (!form.waktuPerjalananMulai) {
          validationErrors.value.waktuPerjalananMulai = 'Waktu perjalanan mulai wajib diisi'
          hasErrors = true
        }

        if (!form.tempatKegiatan || form.tempatKegiatan.trim() === '') {
          validationErrors.value.tempatKegiatan = 'Tempat kegiatan wajib diisi'
          hasErrors = true
        }

        if (!form.keperluan || form.keperluan.trim() === '') {
          validationErrors.value.keperluan = 'Keperluan wajib diisi'
          hasErrors = true
        }

        // Validate perwakilan dinas
        const validPerwakilan = form.perwakilanDinas.filter(item => {
          return item.userId || item.isNonPengurus
        })
        if (validPerwakilan.length === 0) {
          validationErrors.value.perwakilanDinas = 'Minimal harus ada 1 perwakilan dinas'
          hasErrors = true
        }

        // Validate individual non-pengurus fields
        for (const item of form.perwakilanDinas) {
          if (item.isNonPengurus) {
            if (!item.namaNonPengurus || item.namaNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'Nama non-pengurus wajib diisi'
              hasErrors = true
              break
            }
            if (!item.jabatanNonPengurus || item.jabatanNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'Jabatan non-pengurus wajib diisi'
              hasErrors = true
              break
            }
            if (!item.npaNonPengurus || item.npaNonPengurus.trim() === '') {
              validationErrors.value.perwakilanDinas = 'NPA non-pengurus wajib diisi'
              hasErrors = true
              break
            }
          }
        }
      } else if (isLainnya.value) {
        // Surat Lainnya validation (manual nomor)
        if (!form.namaKegiatan || form.namaKegiatan.trim() === '') {
          validationErrors.value.perihal = 'Perihal wajib diisi'
          hasErrors = true
        }

        // Check if Upload File method - validate files instead of body
        if (isUploadFile.value) {
          if (uploadedFiles.value.length === 0) {
            validationErrors.value.uploadedFiles = 'Minimal 1 file harus diupload'
            hasErrors = true
          }
        } else {
          if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
            validationErrors.value.body = 'Isi surat wajib diisi'
            hasErrors = true
          }
        }
      } else {
        // Other surat types (auto-numbering + rich text)
        if (!form.namaKegiatan || form.namaKegiatan.trim() === '') {
          validationErrors.value.perihal = 'Perihal wajib diisi'
          hasErrors = true
        }

        // Check if Upload File method - validate files instead of body
        if (isUploadFile.value) {
          if (uploadedFiles.value.length === 0) {
            validationErrors.value.uploadedFiles = 'Minimal 1 file harus diupload'
            hasErrors = true
          }
        } else {
          if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
            validationErrors.value.body = 'Isi surat wajib diisi'
            hasErrors = true
          }
        }
      }

      return !hasErrors
    }

    // Prepare form data for API
    const prepareFormData = (isDraft = false) => {
      // If Upload File is selected, set bentukSurat to Hard copy
      const bentukSuratValue = isUploadFile.value ? 'Hard copy' : form.bentukSurat

      const baseData = {
        jenisSurat: form.jenisSurat,
        nomorSurat: form.nomorSurat,
        sifatSurat: form.sifatSurat,
        bentukSurat: bentukSuratValue,
        metodePembuatan: form.metodePembuatan,
        tanggalSurat: formatForAPI(form.tanggalSurat),
        penandatanganKiriId: form.penandatanganKiriId,
        penandatanganKananId: form.penandatanganKananId || '', // Empty string to explicitly remove
        verifikatorIds: form.verifikator.filter(item => item.userId).map(item => item.userId),
        isDraft: isDraft,
      }

      // If Upload File is selected and there are new files, return FormData
      if (isUploadFile.value && uploadedFiles.value.length > 0) {
        const formData = new FormData()

        // Append all base data
        Object.keys(baseData).forEach(key => {
          const value = baseData[key]
          if (value !== null && value !== undefined) {
            if (Array.isArray(value)) {
              // For arrays, append each item
              value.forEach((item, index) => {
                formData.append(`${key}[${index}]`, item)
              })
            } else {
              formData.append(key, value)
            }
          }
        })

        // Append files
        uploadedFiles.value.forEach(fileObj => {
          if (fileObj.file) {
            formData.append('files', fileObj.file)
          }
        })

        // Append additional fields based on type
        if (isSuratTugas.value) {
          formData.append('suratUndanganAcaraId', form.suratUndanganAcaraId || '')
          formData.append('namaKegiatan', form.namaKegiatan || '')
          formData.append(
            'waktuPerjalananMulai',
            formatDateTimeForAPI(form.waktuPerjalananMulai) || '',
          )
          formData.append(
            'waktuPerjalananSelesai',
            formatDateTimeForAPI(form.waktuPerjalananSelesai) || '',
          )
          formData.append('tempatKegiatan', form.tempatKegiatan || '')
          formData.append('keperluan', form.keperluan || '')
          // Append perwakilan dinas
          const perwakilanData = form.perwakilanDinas
            .filter(item => item.userId || item.isNonPengurus)
            .map(item => {
              if (item.isNonPengurus) {
                return {
                  isNonPengurus: true,
                  namaNonPengurus: item.namaNonPengurus,
                  jabatanNonPengurus: item.jabatanNonPengurus,
                  npaNonPengurus: item.npaNonPengurus,
                }
              } else {
                return {
                  userId: item.userId,
                  isNonPengurus: false,
                }
              }
            })
          formData.append('perwakilanDinas', JSON.stringify(perwakilanData))
        } else {
          formData.append('suratUndanganAcaraId', form.suratUndanganAcaraId || '')
          formData.append('namaKegiatan', form.namaKegiatan || '')
        }

        return formData
      }

      // Otherwise return JSON
      if (isSuratTugas.value) {
        // Surat Tugas specific fields
        return {
          ...baseData,
          suratUndanganAcaraId: form.suratUndanganAcaraId || null,
          namaKegiatan: form.namaKegiatan,
          waktuPerjalananMulai: formatDateTimeForAPI(form.waktuPerjalananMulai),
          waktuPerjalananSelesai: formatDateTimeForAPI(form.waktuPerjalananSelesai),
          tempatKegiatan: form.tempatKegiatan,
          keperluan: form.keperluan,
          perwakilanDinas: form.perwakilanDinas
            .filter(item => item.userId || item.isNonPengurus)
            .map(item => {
              if (item.isNonPengurus) {
                return {
                  isNonPengurus: true,
                  namaNonPengurus: item.namaNonPengurus,
                  jabatanNonPengurus: item.jabatanNonPengurus,
                  npaNonPengurus: item.npaNonPengurus,
                }
              } else {
                return {
                  userId: item.userId,
                  isNonPengurus: false,
                }
              }
            }),
        }
      } else if (isLainnya.value) {
        // Surat Lainnya - manual nomor, has rich text body
        return {
          ...baseData,
          suratUndanganAcaraId: form.suratUndanganAcaraId || null,
          namaKegiatan: form.namaKegiatan, // Used as perihal/title
          nomorSuratManual: form.nomorSurat, // Manual entry saved separately
          body: form.body, // HTML content from CKEditor
        }
      } else {
        // Other types - auto-numbering, has rich text body
        return {
          ...baseData,
          suratUndanganAcaraId: form.suratUndanganAcaraId || null,
          namaKegiatan: form.namaKegiatan, // Used as perihal/title
          body: form.body, // HTML content from CKEditor
        }
      }
    }

    // Handle submit (submit for verification)
    const handleSubmit = async () => {
      try {
        // Run comprehensive validation
        if (!validateAllFields()) {
          notification.error({
            message: 'Validasi Gagal',
            description: 'Mohon lengkapi semua field yang wajib diisi',
            duration: 5,
          })
          return
        }

        const id = route.params.id
        const formData = prepareFormData(false)

        const result = await store.dispatch('suratKeluar/updateSuratKeluar', { id, formData })

        if (result.status === 'success') {
          notification.success({ message: 'Surat berhasil disimpan dan dikirim untuk verifikasi' })
          router.push('/surat-keluar')
        }
      } catch (error) {
        console.error('Error submitting surat:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal menyimpan surat' })
      }
    }

    // Handle save as draft
    const handleSaveAsDraft = async () => {
      try {
        const id = route.params.id
        const formData = prepareFormData(true)

        const result = await store.dispatch('suratKeluar/updateSuratKeluar', { id, formData })

        if (result.status === 'success') {
          notification.success({ message: 'Draft surat berhasil disimpan' })
          await store.dispatch('suratKeluar/fetchSuratDetail', id)
          populateForm(store.state.suratKeluar.currentSurat)
        }
      } catch (error) {
        console.error('Error saving draft:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal menyimpan draft' })
      }
    }

    // Handle save changes (for non-draft status) - Show confirmation modal
    const handleSaveChanges = async () => {
      try {
        // Run comprehensive validation
        if (!validateAllFields()) {
          notification.error({
            message: 'Validasi Gagal',
            description: 'Mohon lengkapi semua field yang wajib diisi',
            duration: 5,
          })
          return
        }

        // Show confirmation modal
        updateModalVisible.value = true
      } catch (error) {
        console.error('Validation error:', error)
        notification.error({
          message: 'Gagal memvalidasi form',
          description: 'Terjadi kesalahan saat memvalidasi formulir',
          duration: 5,
        })
      }
    }

    // Confirm update (called from modal)
    const confirmUpdate = async () => {
      try {
        const id = route.params.id
        const formData = prepareFormData(false)

        const result = await store.dispatch('suratKeluar/updateSuratKeluar', { id, formData })

        if (result.status === 'success') {
          updateModalVisible.value = false
          notification.success({ message: 'Perubahan berhasil disimpan' })
          await store.dispatch('suratKeluar/fetchSuratDetail', id)
          // Don't repopulate form - keep user's current state including newly added rows
          // User can manually reload page if they want to see the refreshed data
        }
      } catch (error) {
        console.error('Error saving changes:', error)
        notification.error({
          message: error.response?.data?.message || 'Gagal menyimpan perubahan',
        })
      }
    }

    // Handle delete or cancel - opens the modal
    const handleDeleteOrCancel = () => {
      cancelReason.value = ''
      cancelModalVisible.value = true
    }

    // Confirm delete or cancel surat - called from modal
    const confirmDeleteOrCancel = async () => {
      // Validate cancel reason for non-draft status
      if (!isDraft.value && !cancelReason.value?.trim()) {
        notification.warning({
          message: 'Alasan pembatalan harus diisi',
          description: 'Mohon isi alasan pembatalan surat sebelum melanjutkan.',
        })
        return
      }

      cancelLoading.value = true
      const id = route.params.id

      // Safeguard: ensure id is a string
      if (!id || typeof id !== 'string') {
        console.error('Invalid surat ID:', id)
        notification.error({ message: 'ID surat tidak valid' })
        cancelLoading.value = false
        return
      }

      try {
        const result = await store.dispatch('suratKeluar/cancelSuratKeluar', {
          id: id,
          alasan: isDraft.value ? null : cancelReason.value || null,
        })

        if (result.status === 'success') {
          cancelModalVisible.value = false
          const action = result.data?.action
          if (action === 'deleted') {
            notification.success({ message: 'Draft surat berhasil dihapus.' })
          } else {
            notification.success({ message: 'Surat keluar berhasil dibatalkan.' })
          }
          router.push('/surat-keluar')
        } else {
          notification.error({ message: result.message || 'Gagal memproses surat' })
        }
      } catch (error) {
        console.error('Error processing surat:', error)
        notification.error({ message: error.response?.data?.message || 'Gagal memproses surat' })
      } finally {
        cancelLoading.value = false
      }
    }

    // Handle duplicate surat
    const handleDuplicateSurat = () => {
      const id = route.params.id
      router.push('/surat-keluar/create?duplicateFrom=' + id)
    }

    // Handle preview for Surat Lainnya
    const handlePreviewLainnya = async () => {
      // Only validate body content
      if (!form.body || form.body.trim() === '' || form.body === '<p></p>') {
        notification.warning({
          message: 'Preview Tidak Dapat Ditampilkan',
          description: 'Mohon isi konten surat terlebih dahulu',
          duration: 4,
        })
        return
      }

      // Prepare preview data for backend
      const previewData = {
        jenisSurat: form.jenisSurat || 'Surat Lainnya',
        nomorSurat: form.nomorSurat || '[NOMOR SURAT]',
        sifatSurat: form.sifatSurat,
        bentukSurat: form.bentukSurat,
        namaKegiatan: form.namaKegiatan || 'Surat Lainnya',
        tanggalSurat: form.tanggalSurat
          ? form.tanggalSurat.format('DD-MM-YYYY')
          : moment().format('DD-MM-YYYY'),
        penandatanganKiriId: form.penandatanganKiriId || null,
        penandatanganKananId: form.penandatanganKananId || null,
        body: form.body,
      }

      try {
        const response = await suratKeluarAPI.previewLainnyaPDF(previewData)

        // Revoke previous blob URL if exists
        if (currentBlobUrl) {
          window.URL.revokeObjectURL(currentBlobUrl)
          currentBlobUrl = null
        }

        const blob = new Blob([response.data], { type: 'application/pdf' })
        const blobUrl = window.URL.createObjectURL(blob)
        currentBlobUrl = blobUrl // Store for cleanup

        // IMPORTANT: Close modal first to ensure clean state
        pdfModalVisible.value = false

        // Wait for modal to fully close, then set path and open
        await nextTick()

        // Set modal state
        pdfModalTitle.value = `Preview Surat - ${form.namaKegiatan || 'Surat Lainnya'}`
        pdfModalPath.value = blobUrl

        // Small delay to ensure path is set before opening
        await nextTick()

        pdfModalVisible.value = true
      } catch (error) {
        console.error('Preview Lainnya PDF error:', error)
        notification.error({
          message: 'Gagal Membuat Preview',
          description:
            error.response?.data?.message || 'Terjadi kesalahan saat membuat preview PDF',
          duration: 5,
        })
      }
    }

    // Handle cancel surat
    const handleCancelSurat = () => {
      Modal.confirm({
        title: 'Batalkan Surat',
        content:
          'Apakah Anda yakin ingin membatalkan surat ini? Tindakan ini tidak dapat dibatalkan.',
        okText: 'Batalkan',
        okType: 'danger',
        cancelText: 'Kembali',
        onOk: async () => {
          try {
            cancelLoading.value = true
            const id = route.params.id
            const result = await store.dispatch('suratKeluar/cancelSuratKeluar', id)

            if (result.status === 'success') {
              notification.success({ message: 'Surat berhasil dibatalkan' })
              router.push('/surat-keluar')
            }
          } catch (error) {
            console.error('Error canceling surat:', error)
            notification.error({
              message: error.response?.data?.message || 'Gagal membatalkan surat',
            })
          } finally {
            cancelLoading.value = false
          }
        },
      })
    }

    // Initialize
    onMounted(async () => {
      try {
        const id = route.params.id
        await Promise.all([
          store.dispatch('suratKeluar/fetchPengurusOptions'),
          store.dispatch('suratKeluar/fetchSuratMasukOptions'),
          store.dispatch('suratKeluar/fetchSuratDetail', id),
        ])

        if (store.state.suratKeluar.currentSurat) {
          const data = store.state.suratKeluar.currentSurat
          const isOwnerCheck = data.creator?.id === currentUserId
          const canEdit =
            currentUserRole === 'super_admin' || (currentUserRole === 'sekretaris' && isOwnerCheck)

          if (!canEdit) {
            notification.warning({
              message: 'Anda hanya dapat melihat surat ini karena bukan pengaju surat',
            })
            router.replace(`/surat-keluar/${id}/view`)
            return
          }
          populateForm(data)

          // Initialize nomor surat for edit mode
          // This sets the current number as baseline (allows increment to find available numbers)
          // and prevents decrementing below the current number
          if (data.jenisSurat !== 'Lainnya' && data.nomorSurat) {
            setNomorSuratForEdit(data.nomorSurat, data.jenisSurat)
            // DON'T sync back - keep the value from database that was set by populateForm
            // form.nomorSurat is already set correctly by populateForm(data) above
          }

          // Mark form as initialized - this allows watcher to work for manual changes
          // but prevents it from fetching new nomor surat during initial load
          formInitialized.value = true
        }
      } finally {
        pageLoading.value = false
      }
    })

    // Watch for jenisSurat changes
    watch(
      () => form.jenisSurat,
      async (newJenisSurat, oldJenisSurat) => {
        // FIX 2: Ensure strictly initialized, and use 'oldJenisSurat' check
        // Since initial value is now null, 'oldJenisSurat' will be null on first load,
        // causing this block to SKIP, thus preserving the number from populateForm.
        if (!formInitialized.value) return

        if (oldJenisSurat && newJenisSurat !== oldJenisSurat) {
          if (newJenisSurat === 'Surat Tugas') {
            await fetchLatestNomorSurat('Surat Tugas')
            form.nomorSurat = nomorSuratRef.value
          } else if (newJenisSurat === 'Lainnya') {
            form.nomorSurat = ''
          } else {
            await fetchLatestNomorSurat(newJenisSurat)
            form.nomorSurat = nomorSuratRef.value
          }
        }
      },
    )

    // Watch for modal close to cleanup blob URL
    watch(pdfModalVisible, isVisible => {
      if (!isVisible && currentBlobUrl) {
        // Clean up blob URL when modal closes
        setTimeout(() => {
          if (currentBlobUrl) {
            window.URL.revokeObjectURL(currentBlobUrl)
            currentBlobUrl = null
          }
        }, 100) // Small delay to ensure modal animation completes
      }
    })

    return {
      formRef,
      form,
      rules,
      validationErrors,
      suratData,
      pageTitle,
      pageLoading,
      submitLoading,
      draftLoading,
      cancelLoading,
      cancelModalVisible,
      cancelReason,
      updateModalVisible,
      canDecrement,
      canDeleteOrCancel,
      isDraft,
      undanganAcaraOptions,
      isSuratTugas,
      isNotSuratTugas,
      isUploadFile,
      isLainnya,
      nomorSuratError,
      nomorSuratChecking,
      checkNomorSuratUnique,

      // PDF Preview Modal
      pdfModalVisible,
      pdfModalTitle,
      pdfModalPath,

      // TinyMCE Config
      apiKey,
      suratLainnyaInit,

      filterOption,
      getStatusStyle,
      addPerwakilanRow,
      removePerwakilanRow,
      addVerifikatorRow,
      removeVerifikatorRow,
      incrementNomorSurat,
      decrementNomorSurat,
      handleSubmit,
      handleSaveAsDraft,
      handleSaveChanges,
      confirmUpdate,
      handleDeleteOrCancel,
      confirmDeleteOrCancel,
      handleDuplicateSurat,
      handlePreviewLainnya,
      handlePreviewPerwakilan,
      lockedVerifikatorIds,
      shouldHideVerifikatorDelete,

      // File upload
      fileInputRef,
      uploadedFiles,
      triggerFileInput,
      handleDragOver,
      handleDragLeave,
      handleFileDrop,
      handleFileChange,
      removeUploadedFile,
      getUploadedFileUrl,
    }
  },
})
</script>
<style lang="scss" scoped>
.surat-keluar-detail {
  padding: 24px;
  background: #fcfcfc;
  min-height: calc(100vh - 64px);
  position: relative;

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }
}

.page-header {
  margin-bottom: 16px;

  @media (max-width: 768px) {
    margin-bottom: 12px;
  }

  .page-title {
    font-size: 24px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 4px 0;

    @media (max-width: 576px) {
      font-size: 20px;
    }
  }

  .page-subtitle {
    font-size: 16px;
    color: #595959;
    margin: 0 0 8px 0;

    @media (max-width: 576px) {
      font-size: 14px;
    }
  }

  .status-tag {
    border-radius: 50px !important;
    padding: 4px 12px !important;
    font-size: 12px !important;
    font-weight: 700 !important;
  }
}

.action-buttons {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
  flex-wrap: wrap; /* Allows wrapping on small screens */

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 8px;
    margin-bottom: 16px;
  }
}

/* 1. Black Outline Pill (For Batalkan, Duplicate, Draft) */
.btn-pill-black {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #262626; /* Using standard dark grey/black */
  background: white;
  color: #262626;
  padding: 6px 16px; /* Adjusted padding for better visual balance */
  min-height: auto;
  height: auto;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px; /* Space between icon and text */
  box-shadow: none;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  &:hover:not(:disabled) {
    background: #262626 !important;
    border-color: #262626 !important;
    color: white !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

/* 2. Red Outline Pill (For Simpan/Download) */
.btn-pill-red {
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  border: 2px solid #e10f28; /* Your app's red color */
  background: white;
  color: white;
  padding: 6px 16px;
  min-height: auto;
  height: auto;
  line-height: 1.5;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-shadow: none;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }

  &:hover:not(:disabled) {
    background: white !important;
    border-color: #e10f28 !important;
    color: #e10f28 !important;
  }

  &:disabled {
    border-color: #d9d9d9;
    background: #f5f5f5;
    color: #d9d9d9;
  }
}

.form-card {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);

  @media (max-width: 768px) {
    padding: 16px;
  }

  @media (max-width: 576px) {
    padding: 12px;
  }

  // Move required asterisk to the right side of label
  :deep(.ant-form-item-label > label.ant-form-item-required) {
    /* 1. Hide the default asterisk on the LEFT */
    &::before {
      display: none !important;
    }

    /* 2. Create the new asterisk on the RIGHT */
    &::after {
      display: inline-block;
      margin-left: 4px;
      color: #ff4d4f;
      font-size: 14px;
      font-family: SimSun, sans-serif;
      line-height: 1;

      /* CRITICAL: Use !important to override the default colon (:) */
      content: '*' !important;

      /* Ensure it is visible */
      visibility: visible !important;
    }
  }

  .section-title {
    font-size: 18px;
    font-weight: 600;
    color: #262626;
    margin: 0 0 24px 0;
  }

  // Fix placeholder color to be gray instead of black
  :deep(.ant-input::placeholder) {
    color: #bfbfbf !important;
  }

  :deep(.ant-input-number-input::placeholder) {
    color: #bfbfbf !important;
  }

  :deep(.ant-select-selection-placeholder) {
    color: #bfbfbf !important;
  }

  :deep(.ant-picker-input > input::placeholder) {
    color: #bfbfbf !important;
  }

  /* 1. The border color when selected */
  :deep(.ant-radio-checked .ant-radio-inner) {
    border-color: #e10f28 !important;
  }

  /* 2. The inner dot color when selected */
  :deep(.ant-radio-checked .ant-radio-inner::after) {
    background-color: #e10f28 !important;
  }

  /* 3. The border color on hover */
  :deep(.ant-radio-wrapper:hover .ant-radio .ant-radio-inner),
  :deep(.ant-radio:hover .ant-radio-inner) {
    border-color: #e10f28 !important;
  }

  /* 4. (Optional) The focus glow ring/shadow */
  :deep(.ant-radio-input:focus + .ant-radio-inner) {
    box-shadow: 0 0 0 3px rgba(225, 15, 40, 0.2);
  }
}

.form-section {
  margin-bottom: 24px;

  @media (max-width: 768px) {
    margin-bottom: 16px;
  }

  .section-label {
    display: block;
    font-weight: 500;
    color: #262626;
    margin-bottom: 8px;
    font-size: 14px;
  }

  .required {
    color: #ff4d4f;
  }
}

.readonly-input {
  background: #f5f5f5;
  border-color: #d9d9d9;
  color: #262626;
}

.radio-group-horizontal {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
  }

  :deep(.ant-radio-wrapper) {
    margin-right: 0;
  }
}

.full-width {
  width: 100%;
}

.section-description {
  font-size: 12px;
  color: #8c8c8c;
  margin-bottom: 8px;
}

.tinymce-container {
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;

  // Error state
  &.has-error {
    border-color: #ff4d4f;
  }

  :deep(.tox-tinymce) {
    border: none;
    border-radius: 8px;
  }

  :deep(.tox .tox-editor-header) {
    background: #fafafa;
    border-bottom: 1px solid #d9d9d9;
  }

  :deep(.tox .tox-edit-area__iframe) {
    background: white;
  }
}

.verifikator-form-item {
  margin-bottom: 4px !important; // Reduced from default 16px
}

// Override the margin defined inside the child component
:deep(.alur-verifikasi-section.alur-verifikasi-tight) {
  margin-top: 0 !important; // Removed the top margin (was 24px)
}

.preview-section {
  margin-top: 16px;
  display: flex;
  justify-content: flex-end;

  @media (max-width: 768px) {
    justify-content: stretch;
  }

  .btn-preview {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid #e10f28;
    max-width: 230px;
    background: white;
    color: #e10f28;
    padding: 6px 12px;
    min-height: auto;
    height: auto; /* Critical: Let padding define the height */
    line-height: 1.5;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &:hover:not(:disabled) {
      background: #e10f28 !important;
      border-color: white !important; /* Keep border visible on black bg */
      color: white !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }
}

.submit-section {
  display: flex;
  gap: 16px;
  padding-top: 24px;
  border-top: 1px solid #f0f0f0;
  align-items: center; /* Ensures vertical alignment of buttons */

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
  }

  /* * We apply the "Other View" styles directly here.
   * NOTE: We removed 'height: 40px' so the padding controls the centering.
   */

  .btn-simpan {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid #e10f28;
    max-width: 230px;
    background: #e10f28;
    color: white;
    padding: 6px 12px; /* This padding now defines the height naturally */
    min-height: auto; /* Override standard Ant Button height if necessary */
    height: auto; /* Critical: Let padding define the height */
    line-height: 1.5; /* Reset line-height to normal */
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &:hover:not(:disabled) {
      background: white !important;
      border-color: #e10f28 !important;
      color: #e10f28 !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }

  .btn-draft {
    border-radius: 50px;
    font-size: 14px;
    font-weight: 700;
    border: 2px solid black;
    max-width: 230px;
    background: white;
    color: black;
    padding: 6px 12px;
    min-height: auto;
    height: auto; /* Critical: Let padding define the height */
    line-height: 1.5;
    transition: all 0.3s;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    @media (max-width: 768px) {
      width: 100%;
      max-width: 100%;
    }

    &:hover:not(:disabled) {
      background: black !important;
      border-color: white !important; /* Keep border visible on black bg */
      color: white !important;
    }

    &:disabled {
      border-color: #98a2b3;
      background: #98a2b3;
      color: white;
    }
  }
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

// Upload Section Styles
.upload-section {
  margin-bottom: 24px;

  .section-description {
    font-size: 12px;
    color: #8c8c8c;
    margin-bottom: 12px;
  }
}

.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;

  &:hover,
  &.drag-over {
    border-color: #e10f28;
    background: #fff1f2;
  }

  .upload-icon {
    margin-bottom: 16px;
  }

  .upload-text {
    font-size: 14px;
    color: #262626;
    margin: 0 0 8px 0;
    font-weight: 500;
  }

  .upload-hint {
    font-size: 12px;
    color: #8c8c8c;
    margin: 0;
  }
}

.uploaded-files-list {
  margin-top: 16px;

  .uploaded-file-item {
    display: flex;
    align-items: center;
    padding: 8px 12px;
    background: #f5f5f5;
    border-radius: 6px;
    margin-bottom: 8px;

    &:last-child {
      margin-bottom: 0;
    }

    .file-icon {
      color: #8c8c8c;
      margin-right: 8px;
    }

    .file-name {
      flex: 1;
      font-size: 14px;
      color: #262626;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .file-name-link {
      flex: 1;
      font-size: 14px;
      color: #1890ff;
      text-decoration: none;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      &:hover {
        text-decoration: underline;
        color: #40a9ff;
      }
    }

    .btn-remove-file {
      padding: 0 4px;
    }
  }
}
</style>

<style lang="scss">
// Cancel Modal Styles (unscoped for modal portal)
.cancel-modal {
  .modal-description {
    color: #595959;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .cancel-reason-field {
    margin-bottom: 32px;

    .ant-form-item-label > label {
      font-weight: 600;
      color: #262626;
      font-size: 14px;
    }

    .ant-input-textarea {
      textarea {
        border-radius: 8px;
        border: 1px solid #d9d9d9;
        padding: 12px;
        font-size: 14px;

        &:focus {
          border-color: #e10f28;
          box-shadow: 0 0 0 2px rgba(225, 15, 40, 0.1);
        }
      }
    }
    /* --- UPDATED SECTION END --- */
  }

  /* --- UPDATED SECTION START --- */
  :deep(.ant-form-item) {
    margin-bottom: 16px;

    .ant-form-item-label > label {
      font-weight: 500;
      color: #262626;
      font-size: 14px;

      /* Logic to move asterisk to the right */
      &.ant-form-item-required {
        /* 1. Hide default LEFT asterisk */
        &::before {
          display: none !important;
        }

        /* 2. Show new RIGHT asterisk */
        &::after {
          display: inline-block;
          margin-left: 4px;
          color: #ff4d4f;
          font-size: 14px;
          font-family: SimSun, sans-serif;
          line-height: 1;
          content: '*' !important;
          visibility: visible !important;
        }
      }
    }
  }

  .modal-buttons {
    display: flex;
    gap: 16px;
    width: 100%;

    /* Force buttons to share width equally (50:50) */
    .btn-pill-black,
    .btn-pill-red {
      flex: 1;
      width: 100%;
      text-align: center;
    }
  }
}

.confirm-modal {
  .modal-description {
    color: #595959;
    font-size: 14px;
    line-height: 1.6;
    margin-bottom: 24px;
  }

  .modal-buttons {
    display: flex;
    gap: 16px;
    width: 100%;

    /* Force buttons to share width equally (50:50) */
    .btn-pill-black,
    .btn-pill-red {
      flex: 1;
      width: 100%;
      text-align: center;
    }
  }
}
</style>
